import { useState } from "react";

import { TextToolState } from "@impero-com/ui";
import { Row } from "@tanstack/react-table";

import { useUpdateControlAssignmentsForUserMutation } from "org/user/queries/useUpdateControlAssignmentsForUserMutation";
import {
    UserOrUserGroupOverview,
    userOrUserGroupToPartialUpdateTarget,
} from "utils/user";

import { buildPatchAssignmentBody } from "./buildPatchAssignmentBody";
import { ControlRow } from "./useUserAssignedControlListTable";

interface UseReplaceControlUserUtilsProps {
    userId: number;
    selectedRows: Row<ControlRow>[];
    deselectAll: () => void;
}

export function useReplaceControlUserUtils({
    userId,
    selectedRows,
    deselectAll,
}: UseReplaceControlUserUtilsProps) {
    const [isModalOpen, setIsModalOpen] = useState(false);
    const { mutate, isPending, error, reset } =
        useUpdateControlAssignmentsForUserMutation(userId);

    const replaceUserToolState: TextToolState =
        selectedRows.length === 0 ? { type: "DISABLED" } : { type: "ENABLED" };

    const replaceUser = (userOrGroup: UserOrUserGroupOverview) => {
        const target = userOrUserGroupToPartialUpdateTarget(userOrGroup);
        mutate(buildPatchAssignmentBody(selectedRows, target), {
            onSuccess: () => {
                deselectAll();
                setIsModalOpen(false);
            },
            // On failure the modal stays open; `error` drives the inline notice.
        });
    };

    return {
        replaceUserToolState,
        isReplaceUserModalOpen: isModalOpen,
        replaceUserError: error,
        // Reset on open (not close) so the notice stays put during the close
        // animation and a stale error can't reappear next time.
        openReplaceUserModal: () => {
            reset();
            setIsModalOpen(true);
        },
        closeReplaceUserModal: () => setIsModalOpen(false),
        replaceUser,
        isReplacingUser: isPending,
    };
}
